import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Typography } from '@mui/material';
import { DataGridPro, gridClasses, GridToolbarQuickFilter } from '@mui/x-data-grid-pro';
import { connect } from 'react-redux';
import useAbortSignal from 'hooks/useAbortSignal';
import { getFilteredGroups } from 'actions/groups';
import { translate } from '@haldor/ui';
import User from '_class/User';

/**
 * Custom toolbar with only the search/quick filter on the right
 */
const CustomToolbar = ({ translate }) => {
	return (
		<Box sx={{ p: 1, display: 'flex', width: '100%' }}>
			<GridToolbarQuickFilter
				placeholder={translate('Search')}
				sx={{ width: '100%' }}
			/>
		</Box>
	);
};

const getEntityUserId = (entity) => {
	if (!entity) return null;
	const rawId = entity.userId ?? entity.id ?? null;
	if (rawId == null || rawId === '') return null;
	return String(rawId);
};

const getRowStudentId = (row) => {
	if (!row) return null;
	const studentLevelId = getEntityUserId(row.student);
	const rowLevelId = row.studentId != null ? String(row.studentId) : null;
	const resolved = studentLevelId ?? rowLevelId;
	if (resolved == null || resolved === '') return null;
	return resolved;
};

/**
 * NewLeaveRequestList
 * Displays leave requests in a MUI DataGridPro.
 * Columns:
 *  - Elev (student full name)
 *  - Ansökan inkom (received date)
 *  - Startdatum (start date)
 *  - Antal skoldagar (number of school days)
 *  - Mentorsgrupper (list of groups)
 *  - Status (badge)
 * Click row to navigate to decision page
 */
const LeaveRequestsTable = ({
	rows = [],
	loading = false,
	onRowClick,
	height = 520,
	translate,
	filteredGroups = [],
	getFilteredGroups,
	currentUser,
	selectedGroupIds = [],
}) => {

	const user = useMemo(() => currentUser ? new User(currentUser) : null, [currentUser]);
	const isStudent = useMemo(() => user?.isStudent?.() || false, [user]);

	// Status -> chip config mapping (English keys that will be translated)
	const statusConfig = useMemo(() => ({
		'approved': { label: translate('Approved'), sx: { bgcolor: '#4BAF4D', color: '#ffffff', borderRadius: '5px' } }, // green
		'rejected': { label: translate('Rejected'), sx: { bgcolor: '#DF2D2E', color: '#ffffff', borderRadius: '5px' } }, // red
		'pending decision': { label: translate('Pending decision'), sx: { bgcolor: '#ffffff', color: 'text.primary', border: '1px solid #d0d0d0', borderRadius: '5px' } }, // white with border
		'withdrawn': { label: translate('Withdrawn'), sx: { bgcolor: '#9E9E9E', color: '#ffffff', borderRadius: '5px' } }, // gray
		'revoked': { label: translate('Withdrawn'), sx: { bgcolor: '#9E9E9E', color: '#ffffff', borderRadius: '5px' } }, // gray
	}), []);

	// Columns definition
	const allColumns = useMemo(() => /** @type {any[]} */([
		{
			field: 'student',
			headerName: translate('Student'),
			flex: 1.1,
			minWidth: 150,
			sortable: true,
			filterable: true,
			valueGetter: (params) => {
				const s = params;
				if (!s) return '';
				if (typeof s === 'string') return s;
				return `${s.firstName || ''} ${s.lastName || ''}`.trim();
			},
			renderCell: (params) => {
				const s = params.row.student;
				if (!s) return '';
				return `${s.firstName || ''} ${s.lastName || ''}`.trim();
			},
		},
		{
			field: 'receivedDate',
			headerName: translate('Application sent'),
			flex: 0.9,
			minWidth: 150,
			renderCell: (p) => p.row?.receivedDate ? new Date(p.row.receivedDate).toLocaleDateString() : '',
		},
		{
			field: 'startDate',
			headerName: translate('Start date'),
			flex: 0.9,
			minWidth: 140,
			renderCell: (p) => p.row?.startDate ? new Date(p.row.startDate).toLocaleDateString() : '',
		},
		{
			field: 'mentorGroups',
			headerName: translate('Mentor groups'),
			flex: 1.4,
			minWidth: 200,
			sortable: false,
			filterable: true,
			valueGetter: (params) => {
				const groups = params || [];
				return groups.map(g => g.title || g.name || '').join(' ');
			},
			renderCell: (p) => {
				const groups = p.row.mentorGroups || [];
				if (groups.length === 0) return (
					<Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
						<Typography variant="caption" color="text.secondary">-</Typography>
					</Box>
				);
				return (
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
						{groups.map(g => (
							<div key={g.id}>{g.title || g.name || `#${g.id}`}</div>
						))}
					</Box>
				);
			},
		},
		{
			field: 'status',
			headerName: translate('Status'),
			flex: 0.7,
			minWidth: 150,
			sortable: true,
			filterable: true,
			valueGetter: (params) => {
				const status = params || '';
				return translate(status);
			},
			renderCell: (p) => {
				const status = p.row?.status || '';
				const raw = status.toString().toLowerCase();
				const cfg = statusConfig[raw];
				if (cfg) {
					return <Chip label={cfg.label} size="small" sx={cfg.sx} />;
				} else {
					// Fallback: show translated status if no config matches
					return <Chip label={translate(status)} size="small" sx={{ bgcolor: '#f5f5f5', color: 'text.primary', borderRadius: '5px' }} />;
				}
			}
		},
	]), [statusConfig]);

	const columns = useMemo(() => {
		// Hide mentorGroups column for students
		if (user && user.isStudent && typeof user.isStudent === 'function' && user.isStudent()) {
			return allColumns.filter(c => c.field !== 'mentorGroups');
		}
		return allColumns;
	}, [allColumns, user]);


	const signal = useAbortSignal();

	// Fetch mentor groups for all unique student userIds in rows
	useEffect(() => {
		// Only skip when we positively know the current user is a student.
		if (currentUser && isStudent) return;

		const userIds = Array.from(
			new Set(
				(rows || [])
					.map(r => getRowStudentId(r))
					.filter(Boolean)
			)
		);

		// Wait until rows have student IDs before calling the API
		if (userIds.length === 0) return;

		if (getFilteredGroups) {
			const payload = {
				userIds,
				query: '',
				filter: 'MENTOR_GROUP;ACTIVE',
				sortBy: '',
				pageIndex: null,
				pageSize: null,
				expand: ['MEMBERS']
			};
			// Pass the abort signal first so the action can cancel the request on unmount
			getFilteredGroups(signal, payload);
		}
	}, [rows, getFilteredGroups, currentUser, isStudent]);

	// Map mentor groups to each row based on student membership
	const mappedRows = useMemo(() => {
		if (!rows) return rows;

		// If current user is a student, do not include mentorGroups
		if (user && user.isStudent && user.isStudent()) {
			return rows.map(r => ({ ...r, mentorGroups: [] }));
		}

		// Preserve groups from row data until filteredGroups are available.
		if (!Array.isArray(filteredGroups) || filteredGroups.length === 0) return rows;

		const result = rows.map(row => {
			const studentId = getRowStudentId(row);
			if (!studentId) return row;
			const mentorGroups = filteredGroups.filter(
				g => Array.isArray(g.members) && g.members.some(m => getEntityUserId(m) === studentId)
			);
			// If no match found, keep existing mentorGroups from row data.
			if (mentorGroups.length === 0) return row;
			return { ...row, mentorGroups };
		});
		return result;
	}, [rows, filteredGroups, user]);

	const displayedRows = useMemo(() => {
		if (!selectedGroupIds || selectedGroupIds.length === 0) return mappedRows;
		const selectedGroupIdSet = new Set(selectedGroupIds.map((id) => String(id)));
		return mappedRows.filter(r => {
			const rowGroupIds = (r.mentorGroups || []).map(g => String(g.id));
			return rowGroupIds.some(id => selectedGroupIdSet.has(id));
		});
	}, [mappedRows, selectedGroupIds]);

	return (
		<Box sx={{ width: '100%' }}>
			<DataGridPro
				rows={displayedRows}
				columns={columns}
				getRowId={(r) => r.id}
				loading={loading}
				disableColumnMenu
				disableRowSelectionOnClick
				autosizeOptions={{ includeHeaders: true }}
				slots={{ toolbar: CustomToolbar }}
				slotProps={{
					toolbar: {
						translate,
						quickFilterProps: { debounceMs: 500 }
					}
				}}
				onRowClick={(p) => onRowClick && onRowClick(p.row)}
				initialState={{
					sorting: { sortModel: [{ field: 'receivedDate', sort: 'desc' }] },
					pagination: { paginationModel: { pageSize: 50 } },
				}}
				pageSizeOptions={[25, 50, 100]}
				sx={{
					minHeight: height,
					[`& .${gridClasses.cell}`]: { alignItems: 'center' },
					[`& .${gridClasses.row}`]: { cursor: 'pointer' },
				}}
				localeText={{
					noRowsLabel: 'Inga ansökningar',
					footerRowSelected: (count) => `${count} valda`,
				}}
			/>
		</Box>
	);
};

LeaveRequestsTable.propTypes = {
	rows: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		student: PropTypes.shape({
			firstName: PropTypes.string,
			lastName: PropTypes.string,
		}),
		receivedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
		startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
		schoolDays: PropTypes.number,
		mentorGroups: PropTypes.arrayOf(PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			title: PropTypes.string,
			name: PropTypes.string,
		})),
		decisionSent: PropTypes.bool,
	})),
	loading: PropTypes.bool,
	onRowClick: PropTypes.func,
	height: PropTypes.number,
};


LeaveRequestsTable.defaultProps = {};

export default connect(
	(state) => ({
		translate: translate(state.Languages.translations),
		filteredGroups: state.sectionsV2 && state.sectionsV2.filteredGroups,
		currentUser: state.user && state.user.currentUser,
	}),
	{
		getFilteredGroups
	}
)(LeaveRequestsTable);